import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { FormHelperService } from 'src/app/core/helpers/form-helper/form-helper.service';
import { Appointment } from 'src/app/core/models/appointment.model';
import { CustomerService } from 'src/app/core/models/customer-service.model';
import { EquipmentAttribute } from 'src/app/core/models/equipment-attribute.model';
import { Equipment, EquipmentCategory } from 'src/app/core/models/equipment-category.model';
import { InventoryAttribute, InventoryItem } from 'src/app/core/models/inventory.model';
import { EquipmentService } from 'src/app/core/services/equipment/equipment.service';
import { InventoryFormHelperService } from 'src/app/core/services/inventory/inventory-form-helper.service';
import { TruckInventoryService } from 'src/app/core/services/inventory/truck-inventory.service';
import { TicketProvisioningService } from 'src/app/core/services/ticket-provisioning/ticket-provisioning.service';
import { PrimaryAttributeSearchModalComponent } from '../primary-attribute-search-modal/primary-attribute-search-modal.component';

type SearchModalResponse = OverlayEventDetail<{
	dismissed?: boolean;
	truckInventory: InventoryItem
}>;

@Component({
	selector: 'app-add-truck-inventory-modal',
	templateUrl: './add-truck-inventory-modal.component.html',
	styleUrls: ['./add-truck-inventory-modal.component.scss']
})
export class AddTruckInventoryModalComponent implements OnInit {

	@Input()
	appointment: Appointment;

	@Input()
	prefillOptions: { categoryId: number, equipmentId: number, customerServiceId: number };

	@Input()
	canToggle: boolean;

	@Input()
	isSwap: boolean;

	pageTitle = 'Add Truck Inventory';

	filterForm: FormGroup;
	categoryOptions = [] as EquipmentCategory[];
	equipmentOptions = [] as Equipment[];
	primaryAttributeName: string;

	dynamicAttributesForm: FormGroup;
	dynamicAttributes = [] as EquipmentAttribute[];
	equipmentAttributes = [] as EquipmentAttribute[];
	serviceOptions = [] as CustomerService[];
	serviceOptionDefault = new CustomerService();

	inventoryItem: InventoryItem;
	foundEquipment: boolean = false;
	categorySelected: boolean = false;
	equipmentSelected: boolean = false;
	truckInventory: InventoryItem[];

	constructor(
		public formHelper: FormHelperService,
		public inventoryFormHelper: InventoryFormHelperService,
		private modalController: ModalController,
		private formBuilder: FormBuilder,
		private equipmentService: EquipmentService,
		private truckInventoryService: TruckInventoryService,
		private ticketProvisioningService: TicketProvisioningService,
	) { }

	async ngOnInit(): Promise<void> {
		this.categoryOptions = await this.equipmentService.getCategories();
		this.equipmentAttributes = await this.equipmentService.getAttributes();

		this.filterForm = this.formBuilder.group({
			categoryId: [null, Validators.required],
			equipmentId: [{ value: null, disabled: true }, Validators.required],
			primaryAttribute: [null, Validators.required],
			customerServiceId: [null, Validators.required]
		});

		this.truckInventory = await this.truckInventoryService.getAllTruckInventory();

		if (this.prefillOptions?.categoryId) {
			const { categoryId, equipmentId, customerServiceId } = this.prefillOptions;

			this.filterForm.controls.categoryId.setValue(categoryId);
			await this.onChangeCategory(categoryId);
			if (equipmentId && this.foundEquipment) {
				this.filterForm.controls.equipmentId.setValue(equipmentId);
				this.onChangeEquipment(equipmentId);
			}

			this.filterForm.controls.customerServiceId.setValue(customerServiceId);
		}
	}

	async onChangeCategory(categoryId: number): Promise<void> {
		this.filterForm.controls.primaryAttribute.setValue(null);
		this.primaryAttributeName = null;
		this.filterForm.controls.equipmentId.setValue(null);
		this.filterForm.controls.equipmentId.enable();
		this.inventoryItem = null;
		this.equipmentSelected = false;

		this.equipmentOptions = this.categoryOptions.find(c => c.id === categoryId).equipment.filter(
			e => this.truckInventory.find(a => a.equipId === e.id)
		).sort((a, b) => a.name > b.name ? 1 : -1);

		if (this.equipmentOptions.length > 0) {
			this.foundEquipment = true;
		} else {
			this.foundEquipment = false;
        }
		this.categorySelected = true;
	}

	onChangeEquipment(equipmentId: number): void {
		if (!equipmentId) {
			return this.clearDynamicForm();
		}

		this.setPrimaryAttributeName(this.filterForm.controls.categoryId.value, equipmentId);
	}

	clearDynamicForm(): void {
		this.dynamicAttributes = [];
		this.dynamicAttributesForm = null;
	}

	async setPrimaryAttributeName(categoryId: number, equipmentId: number): Promise<void> {
		const inventory = await this.truckInventoryService.getTruckInventory(categoryId, equipmentId);
		this.equipmentSelected = true;
		if (inventory) {
			this.primaryAttributeName = inventory.attributes[0].name;
		}
	}

	async onClickOpenSearchModal(event: any) {
		event.preventDefault();
		const modal = await this.modalController.create({
			component: PrimaryAttributeSearchModalComponent,
			componentProps: {
				inventoryItems: this.truckInventory
					.filter(i => i.categoryId === this.filterForm.controls.categoryId.value && i.equipId === this.filterForm.controls.equipmentId.value)
					.filter(i => i.attributes[0].value !== null),
				primaryAttributeName: this.primaryAttributeName,
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();
		const response = (await modal.onDidDismiss()) as SearchModalResponse;

		const truckInventory = response?.data?.truckInventory;
		if (truckInventory) {
			this.inventoryItem = truckInventory;
			this.filterForm.controls.primaryAttribute.setValue(truckInventory.attributes[0].value);

			this.setDynamicAttributes(this.equipmentAttributes, this.inventoryItem.attributes);
			this.initDynamicForm(this.dynamicAttributes, this.appointment, this.serviceOptions);
		}
	}

	setDynamicAttributes(equipAttributes: EquipmentAttribute[], invAttributes: InventoryAttribute[]) {
		this.dynamicAttributes = this.inventoryFormHelper.getDynamicAttributes(equipAttributes, invAttributes);
	}

	async initDynamicForm(dynamicAttributes: EquipmentAttribute[], appointment: Appointment, services: CustomerService[]) {
		const { formFields, serviceOptions } = await this.inventoryFormHelper
			.buildDynamicForm(dynamicAttributes, appointment, services);

		this.serviceOptions = serviceOptions;
		this.dynamicAttributesForm = this.formBuilder.group(formFields);
	}

	async onClickAddInventory() {
		this.filterForm = this.formHelper.markAsTouched(this.filterForm);
		this.dynamicAttributesForm = this.formHelper.markAsTouched(this.dynamicAttributesForm);
		if (this.filterForm.invalid || this.dynamicAttributesForm.invalid) {
			await this.formHelper.alertUser();
			return;
		}

		this.inventoryItem.customerServiceId = this.filterForm.controls.customerServiceId.value;
		this.inventoryItem.customerServiceDescription = this.appointment.services.filter(s => s.id == this.inventoryItem.customerServiceId)[0]?.desc;
		const form = this.dynamicAttributesForm.value;
		const attributeUpdates = this.inventoryFormHelper.updateInventoryDynamicAttributes(form, this.dynamicAttributes, this.inventoryItem);

		await this.truckInventoryService.addTruckInventory(this.appointment.id, this.inventoryItem, attributeUpdates);
		await this.ticketProvisioningService.addProvision(this.appointment.id, this.inventoryItem);

		await this.inventoryFormHelper.toastAddSuccess();
		await this.dismiss({ isSwap: this.isSwap });
	}

	async toggleMode(): Promise<void> {
		this.dismiss({ forcedModal: 'WAREHOUSE' });
	}

	async dismiss(data?: any): Promise<void> {
		await this.modalController.dismiss({
			dismissed: true,
			...data,
		});
	}

}
